/**
 * React Hook for AI Image Blending
 * Automatically triggers AI blend when a product is selected
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  fetchProductImageFromBackend,
  fetchQuoteImageFromBackend,
  getProductImageParams,
  getQuoteImageParams,
  locationsToBoxes,
  urlToFile,
  buildResultUrl,
} from "../utils/imageHelpers";
import { fetchResultAsBlobUrl } from "../api";
import {
  usePollTaskStatus,
  useSegmentReplace,
  type ITaskStatus,
} from "./index";
import { queryClient } from "@/lib";
import { AI_SERVICE_QUERY_KEY } from "../constants/queryKey";

interface WindowLocation {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface UseAIBlendOptions {
  /** Property/house image URL */
  propertyImageUrl?: string;
  /** Window locations (rect boxes) */
  windowLocations?: WindowLocation[];

  selectedProductUrl: string;
  /** Whether to enable auto-blend (default: true) */
  enabled?: boolean;
  /** Whether to use online blending (default: true) */
  onlineBlending?: boolean;
  /** Custom color for AI prompt */
  color?: string;
  /**
   * Identity of the currently selected product (e.g. its optionId). Different
   * products can share the same base thumbnail URL (they differ by colour/
   * options, not the base image), so selectedProductUrl alone can stay
   * unchanged across a product switch and never re-trigger the effect below.
   * Passing the product's identity here forces a re-blend whenever the
   * product actually changes, regardless of whether the URL did.
   */
  productKey?: string;
}

interface AIBlendState {
  /** Whether AI blend is in progress */
  isBlending: boolean;
  /** Current task ID */
  taskId: string | null;
  /** Blended image blob URL for in-session display only (may be revoked) */
  blendedImageUrl: string | null;
  /** Raw AI service URL — stable for cart persistence */
  blendedStorageUrl: string | null;
  /** Error message (if any) */
  error: string | null;
  /** Current task status */
  taskStatus: ITaskStatus | null;
  /** Whether blend is complete */
  isComplete: boolean;
  onLineBlendImageUrl?: string | null;
  /** Raw AI service URL for online blend — stable for cart persistence */
  onlineBlendStorageUrl?: string | null;
}

export const useBlendAiService = (options: UseAIBlendOptions) => {
  const {
    propertyImageUrl,
    windowLocations = [],
    selectedProductUrl,
    onlineBlending = false,
    color = "default",
    enabled,
    productKey,
  } = options;
  const [state, setState] = useState<AIBlendState>({
    isBlending: false,
    taskId: null,
    blendedImageUrl: null,
    blendedStorageUrl: null,
    error: null,
    taskStatus: null,
    isComplete: false,
  });

  const segmentReplace = useSegmentReplace();
  // Track current blob URLs so we can revoke them when new results arrive
  const offlineBlobUrlRef = React.useRef<string | null>(null);
  const onlineBlobUrlRef = React.useRef<string | null>(null);

  // Poll task status
  const taskStatus = usePollTaskStatus(state.taskId || "", {
    enabled: !!state.taskId && !state.isComplete,
    onSuccess: async (data) => {
      if (!data.result_url) return;

      // Fetch once via the Axios instance and cache as blob URL.
      // This prevents failures caused by URL expiry or CORS on direct <img> loads.
      const rawUrl = buildResultUrl(data.result_url);
      const blobUrl = rawUrl.startsWith('blob:')
        ? rawUrl
        : await fetchResultAsBlobUrl(rawUrl).catch(() => rawUrl);

      if (onlineBlending) {
        if (onlineBlobUrlRef.current?.startsWith('blob:')) {
          URL.revokeObjectURL(onlineBlobUrlRef.current);
        }
        onlineBlobUrlRef.current = blobUrl;
      } else {
        if (offlineBlobUrlRef.current?.startsWith('blob:')) {
          URL.revokeObjectURL(offlineBlobUrlRef.current);
        }
        offlineBlobUrlRef.current = blobUrl;
      }

      setState((prev) => ({
        ...prev,
        isBlending: false,
        isComplete: true,
        taskStatus: data,
        // Display URL: blob for in-session <img> rendering
        blendedImageUrl: onlineBlending ? prev.blendedImageUrl : blobUrl,
        onLineBlendImageUrl: onlineBlending ? blobUrl : prev.onLineBlendImageUrl,
        // Storage URL: raw AI URL — valid for cart persistence (not a blob)
        blendedStorageUrl: onlineBlending ? prev.blendedStorageUrl : rawUrl,
        onlineBlendStorageUrl: onlineBlending ? rawUrl : prev.onlineBlendStorageUrl,
      }));
    },
    onError: (error) => {
      const errorMessage = error?.message || "AI blend processing failed";
      setState((prev) => ({
        ...prev,
        isBlending: false,
        isComplete: true, // Mark as complete even on error to stop polling
        error: errorMessage,
        taskStatus: null,
      }));
    },
  });

  // Update task status in state
  useEffect(() => {
    if (taskStatus.data) {
      setState((prev) => ({
        ...prev,
        taskStatus: taskStatus.data || null,
      }));
    }
  }, [taskStatus.data]);

  // Clear the previous blend result whenever the underlying template URL
  // changes — deliberately NOT gated on `enabled`. selectedProductUrl already
  // reflects the live grill/colorize/dbr pipeline state (e.g. it drops the
  // grill output the instant grillNeedsToRun goes false), but `enabled` can
  // still be false for a render or two while a sibling stage (e.g.
  // colorizeBlend.isApplying, retriggered by the same pipelineSignature
  // change) is mid-settle. If the reset were gated on `enabled` too, that
  // window lets this effect skip entirely, leaving the STALE blendedImageUrl
  // (e.g. still containing a grill the user just set to None) in place —
  // and since callers put blendedImageUrl first in their `??` fallback
  // chain, it masks the already-correct colorize/grill/dbr output
  // underneath until some later blend happens to complete.
  React.useEffect(() => {
    if (!selectedProductUrl || !propertyImageUrl) return;
    if (segmentReplace.controllerRef.current) {
      segmentReplace.controllerRef.current.abort();
    }
    // Cancel ongoing task status queries
    queryClient.cancelQueries({
      queryKey: [AI_SERVICE_QUERY_KEY.taskStatus],
    });
    if (offlineBlobUrlRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(offlineBlobUrlRef.current);
      offlineBlobUrlRef.current = null;
    }
    if (onlineBlobUrlRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(onlineBlobUrlRef.current);
      onlineBlobUrlRef.current = null;
    }
    setState((prev) => ({
      ...prev,
      taskStatus: null,
      isComplete: true,
      isBlending: false,
      taskId: null,
      blendedImageUrl: null,
      blendedStorageUrl: null,
      onLineBlendImageUrl: null,
      onlineBlendStorageUrl: null,
    }));
  }, [selectedProductUrl, propertyImageUrl, color]);

  // Actually kick off the new blend once the whole pipeline says it's safe to
  // (product selected, house image present, upstream DBR/colorize/grill
  // stages settled).
  React.useEffect(() => {
    if (selectedProductUrl && propertyImageUrl && enabled) {
      handleAiBlendProcessing();
    }
  }, [selectedProductUrl, propertyImageUrl, enabled, color, productKey]);

  React.useEffect(() => {
    if (selectedProductUrl && propertyImageUrl && enabled) {
      if (onlineBlending) {
        handleAiBlendProcessing();
      }
    }
  }, [onlineBlending, enabled, color, productKey]);

  const handleAiBlendProcessing = async () => {
    setState((prev) => ({
      ...prev,
      isBlending: true,
      error: null,
    }));
    try {
      const templateFile = await getProductFile();
      const houseFile = await getHouseFile(propertyImageUrl as string);
      const points = locationsToBoxes(windowLocations);
      const result = await segmentReplace.mutateAsync({
        house_image: houseFile,
        template_image: templateFile,
        boxes: points,
        online_blending: onlineBlending,
        color,
      });
      setState((prev) => ({
        ...prev,
        taskId: result.task_id,
        isComplete: false, // Enable polling
        error: null,
      }));
    } catch (error) {
      // CanceledError means the request was intentionally aborted because the
      // product URL changed (DBR/grill pipeline step completed and restarted the
      // blend with the updated image). Silently ignore — the new request is
      // already in flight and will update state when it completes.
      if ((error as any)?.code === 'ERR_CANCELED' || (error as any)?.message === 'canceled') {
        return;
      }
      const errorMessage =
        error instanceof Error ? error.message : "AI blend processing failed";
      setState((prev) => ({
        ...prev,
        isBlending: false,
        isComplete: true,
        error: errorMessage,
      }));
    }
  };

  // Fetch the house/property image, routing through the backend proxy when
  // the URL is a blob-storage quote image (avoids CORS restrictions).
  const getHouseFile = async (url: string): Promise<File> => {
    const quoteParams = getQuoteImageParams(url);
    if (quoteParams) {
      try {
        return await fetchQuoteImageFromBackend(quoteParams.quoteGuid, quoteParams.fileName);
      } catch (_: any) {
        // Fall back to direct fetch if backend proxy fails
      }
    }
    return await urlToFile(url, "house.jpg");
  };

  const getProductFile = async () => {
    // Only call the product backend for actual product image URLs.
    // Grill result URLs (e.g. /api/download/...) are fetched directly.
    if (selectedProductUrl.includes("productdetails")) {
      const productParams = getProductImageParams(selectedProductUrl as string);
      try {
        return await fetchProductImageFromBackend(
          productParams.productGuid,
          productParams.fileName,
        );
      } catch (_: any) {
        // Fall back to direct URL fetch if backend fails
      }
    }
    return await urlToFile(selectedProductUrl, "template.png");
  };

  /**
   * Reset blend state
   */
  const reset = useCallback(() => {
    if (offlineBlobUrlRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(offlineBlobUrlRef.current);
      offlineBlobUrlRef.current = null;
    }
    if (onlineBlobUrlRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(onlineBlobUrlRef.current);
      onlineBlobUrlRef.current = null;
    }
    setState({
      isBlending: false,
      taskId: null,
      blendedImageUrl: null,
      blendedStorageUrl: null,
      error: null,
      taskStatus: null,
      isComplete: false,
    });
  }, []);

  return {
    ...state,
    reset,
    // Expose raw task status query for advanced usage
    taskStatusQuery: taskStatus,
  };
};
